<template>
	<figure
		ref="figureRef"
		:class="['gallery-grid-item', blockClassModifiers]"
		:title="_copyright"
		:tabindex
		@focus="scrollIntoView"
	>
		<figcaption>{{ alt }}</figcaption>
		<img
			ref="imageRef"
			:class="['gallery-grid-item__image', { 'gallery-grid-item__image--loaded': isLoaded }]"
			:data-src="isVideo ? preview : content"
			:alt="alt ?? _copyright"
			@load="isLoaded = true"
		/>
		<GalleryPlayButton
			v-if="isVideo"
			class="gallery-grid-item__play-button"
		/>
	</figure>
</template>
<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import GalleryPlayButton from './GalleryPlayButton.vue';
import type { GalleryElement } from '@/interfaces/components/galleryElement';

interface FigureGridItemProps {
	content: GalleryElement['content'];
	preview: GalleryElement['preview'];
	type: GalleryElement['type'];
	copyright: GalleryElement['copyright'];
	alt?: GalleryElement['alt'];
	aspectRatio?: GalleryElement['aspectRatio'];
	tabindex?: number;
}

const { tabindex = 1, type, aspectRatio = 1.6, copyright } = defineProps<FigureGridItemProps>();
const isVideo = computed(() => ['youtube', 'vimeo', 'ytube'].includes(type));
const _copyright = computed(() => {
	if (copyright.includes('©')) {
		return copyright;
	}
	if (copyright.includes('&copy;')) {
		return copyright.replace('&copy;', '©');
	}
	return `© ${copyright}`;
});
const isLoaded = ref(false);
const figureRef = useTemplateRef('figureRef');
const imageRef = useTemplateRef('imageRef');

const blockClassModifiers = computed(() => ({ 'gallery-grid-item--video': isVideo, 'gallery-grid-item--loaded': isLoaded.value }));
const _aspectRatio = computed(() => {
	return isLoaded.value ? 'auto' : aspectRatio;
});

const preloadImage = () => {
	if (!imageRef.value) return;
	imageRef.value.src = imageRef.value.getAttribute('data-src') as string;
};
const scrollIntoView = () => {
	if (!figureRef.value) return;
	setTimeout(() => {
		figureRef.value?.scrollIntoView({
			behavior: 'smooth',
			block: 'center',
			inline: 'center',
		});
	}, 30);
};

const observer = new IntersectionObserver(
	function (entries, self) {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				preloadImage();
				self.unobserve(entry.target);
			}
		});
	},
	{
		root: null,
		rootMargin: '40px 0px 40px 0px',
		threshold: 0,
	}
);

onMounted(() => {
	if (!figureRef.value) return;
	observer.observe(figureRef.value);
});
onBeforeUnmount(() => {
	observer.disconnect();
});
</script>

<style lang="scss" scoped>
.gallery-grid-item {
	position: relative;
	padding: 0;
	margin: 0;
	width: 100%;
	cursor: pointer;
	/* stylelint-disable-next-line value-keyword-case */
	aspect-ratio: v-bind(_aspectRatio);
	height: auto;
	transition: background-color 0.2s ease-in;
	border-radius: $border-radius-small;
	display: flex;
	align-items: center;

	@include focus-state($border-radius-small);

	&:not(.gallery-grid-item--loaded) {
		background-color: rgba($color-dark-gray, 20%);
	}

	&--loaded {
		background-color: rgba($color-dark-gray, 0%);
	}

	figcaption {
		@include sr-only;
	}

	&__play-button {
		pointer-events: none;
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}

	&__image {
		width: 100%;
		opacity: 0;
		transition: opacity 0.3s ease-in;
		border-radius: $border-radius-small;

		&--loaded {
			opacity: 1;
		}
	}
}
</style>
